import { useEffect, useState } from 'react';
import './sendMessageModal.css';

export default function SendMessageModal({
    open,
    onClose,
    selectedUsers = [],
    totalUsers = 0,

    sendType,
    setSendType,

    message,
    setMessage,

    // Broadcast filter states
    broadcastFilters,
    setBroadcastFilters,

    // Existing status dropdown from ChatbotUsers
    statusDropdown = [],

    // Count returned from backend / preview API
    filteredUserCount = 0,

    onSend,
    loading = false
}) {
    const [msgType, setMsgType] = useState('broadcast');
    const [selectedTemplate, setSelectedTemplate] = useState('');

    // --------------------------------------------------
    // Reset filters when modal opens
    // --------------------------------------------------
    useEffect(() => {
        if (open) {
            setBroadcastFilters((prev) => ({
                leadStatus: prev?.leadStatus || [],
                window: prev?.window || 'active'
            }));
        }
    }, [open, setBroadcastFilters]);

    if (!open) return null;

    // --------------------------------------------------
    // Template change
    // --------------------------------------------------
    const handleTemplateChange = (e) => {
        const value = e.target.value;

        setSelectedTemplate(value);

        switch (value) {
            case 'trip':
                setMessage('Hi 👋\n\nHere are the trip details you requested.\n\n');
                break;

            case 'payment':
                setMessage('Hi 👋\n\nThis is a reminder to complete your booking payment.');
                break;

            case 'followup':
                setMessage("Hi 👋\n\nJust checking if you're still interested in the trip.");
                break;

            case 'booking':
                setMessage('🎉 Your booking has been confirmed.\n\nThank you for choosing TRVTO.');
                break;

            default:
                setMessage('');
        }
    };

    // --------------------------------------------------
    // Toggle lead status
    // --------------------------------------------------
    const handleLeadStatusChange = (statusValue) => {
        setBroadcastFilters((prev) => {
            const currentStatuses = prev?.leadStatus || [];

            const alreadySelected = currentStatuses.includes(statusValue);

            return {
                ...prev,

                leadStatus: alreadySelected
                    ? currentStatuses.filter((status) => status !== statusValue)
                    : [...currentStatuses, statusValue]
            };
        });
    };

    // --------------------------------------------------
    // Change 24hr window
    // --------------------------------------------------
    const handleWindowChange = (windowValue) => {
        setBroadcastFilters((prev) => ({
            ...prev,
            window: windowValue
        }));
    };

    // --------------------------------------------------
    // Recipient text
    // --------------------------------------------------
    const getRecipientCount = () => {
        if (sendType === 'selected') {
            return selectedUsers.length;
        }

        if (sendType === 'filter') {
            return filteredUserCount;
        }

        return totalUsers;
    };

    const getRecipientText = () => {
        if (sendType === 'selected') {
            return `${selectedUsers.length} selected users`;
        }

        if (sendType === 'filter') {
            return `${filteredUserCount} users`;
        }

        return `${totalUsers} users`;
    };

    return (
        <div className='sendModalOverlay'>
            <div className='sendModalBox'>
                {/* ==================================================
                    HEADER
                ================================================== */}

                <div className='sendModalHeader'>
                    <div>
                        <h2>✈️ Send Message</h2>

                        <p>Send a message to {getRecipientText()}</p>
                    </div>

                    <button className='closeModalBtn' onClick={onClose}>
                        ✕
                    </button>
                </div>

                {/* ==================================================
                    MESSAGE TYPE TABS
                ================================================== */}

                <div className='sendTabs'>
                    <button
                        className={msgType === 'broadcast' ? 'activeTab' : ''}
                        onClick={() => {
                            setMsgType('broadcast');
                            setSelectedTemplate('');
                            setMessage('');
                        }}
                    >
                        Broadcast Message
                    </button>

                    <button
                        className={msgType === 'template' ? 'activeTab' : ''}
                        onClick={() => setMsgType('template')}
                    >
                        Message Template
                    </button>
                </div>

                {/* ==================================================
                    SEND TO
                ================================================== */}

                <div className='sendLabel'>Send To</div>

                <div className='sendTypeCards'>
                    {/* ----------------------------------------------
                        SELECTED USERS
                    ---------------------------------------------- */}

                    <div
                        className={`sendCard ${sendType === 'selected' ? 'selectedCard' : ''}`}
                        onClick={() => setSendType('selected')}
                    >
                        <input type='radio' checked={sendType === 'selected'} readOnly />

                        <div>
                            <h4>Selected Users ({selectedUsers.length})</h4>

                            <span>Only selected users will receive this message</span>
                        </div>
                    </div>

                    {/* ----------------------------------------------
                        FILTERED USERS
                    ---------------------------------------------- */}

                    <div
                        className={`sendCard ${sendType === 'filter' ? 'selectedCard' : ''}`}
                        onClick={() => setSendType('filter')}
                    >
                        <input type='radio' checked={sendType === 'filter'} readOnly />

                        <div>
                            <h4>Filtered Users ({filteredUserCount})</h4>

                            <span>Send only to users matching your filters</span>
                        </div>
                    </div>

                    {/* ----------------------------------------------
                        ALL USERS
                    ---------------------------------------------- */}

                    <div
                        className={`sendCard ${sendType === 'all' ? 'selectedCard' : ''}`}
                        onClick={() => setSendType('all')}
                    >
                        <input type='radio' checked={sendType === 'all'} readOnly />

                        <div>
                            <h4>All Users ({totalUsers})</h4>

                            <span>Send message to every chatbot user</span>
                        </div>
                    </div>
                </div>

                {/* ==================================================
                    FILTER SECTION
                ================================================== */}

                {sendType === 'filter' && (
                    <div className='broadcastFilterBox'>
                        {/* ------------------------------------------
                            LEAD STATUS
                        ------------------------------------------ */}

                        <div className='filterSection'>
                            <div className='sendLabel'>Lead Status</div>

                            <div className='broadcastStatusOptions'>
                                {statusDropdown?.length > 0 ? (
                                    statusDropdown.map((status) => {
                                        const statusValue = status.name;

                                        const isSelected = broadcastFilters?.leadStatus?.includes(statusValue);

                                        return (
                                            <label
                                                key={status._id || status.name}
                                                className={`broadcastStatusOption ${isSelected ? 'active' : ''}`}
                                            >
                                                <input
                                                    type='checkbox'
                                                    checked={isSelected}
                                                    onChange={() => handleLeadStatusChange(statusValue)}
                                                />

                                                <span>{status.displayName || status.name}</span>
                                            </label>
                                        );
                                    })
                                ) : (
                                    <div className='filterEmpty'>No lead statuses available</div>
                                )}
                            </div>
                        </div>

                        {/* ------------------------------------------
                            24 HOUR WINDOW
                        ------------------------------------------ */}

                        <div className='broadcastWindowOptions'>
                            <button
                                type='button'
                                className={`broadcastWindowOption ${
                                    broadcastFilters.window === 'active' ? 'active' : ''
                                }`}
                                onClick={() =>
                                    setBroadcastFilters((prev) => ({
                                        ...prev,
                                        window: 'active'
                                    }))
                                }
                            >
                                <strong>Active</strong>
                                <small>Within 24 hours</small>
                            </button>

                            <button
                                type='button'
                                className={`broadcastWindowOption ${
                                    broadcastFilters.window === 'expired' ? 'active' : ''
                                }`}
                                onClick={() =>
                                    setBroadcastFilters((prev) => ({
                                        ...prev,
                                        window: 'expired'
                                    }))
                                }
                            >
                                <strong>Expired</strong>
                                <small>More than 24 hours</small>
                            </button>

                            <button
                                type='button'
                                className={`broadcastWindowOption ${broadcastFilters.window === 'all' ? 'active' : ''}`}
                                onClick={() =>
                                    setBroadcastFilters((prev) => ({
                                        ...prev,
                                        window: 'all'
                                    }))
                                }
                            >
                                <strong>All</strong>
                                <small>No time filter</small>
                            </button>
                        </div>

                        {/* ------------------------------------------
                            FILTER RESULT
                        ------------------------------------------ */}

                        <div className='selectedFilterInfo'>
                            <div>
                                <strong>{getRecipientCount()} </strong>

                                <span> users will receive this message</span>
                            </div>

                            {broadcastFilters?.leadStatus?.length > 0 && (
                                <div className='filterSummary'>
                                    <strong>Status:</strong> {broadcastFilters.leadStatus.join(', ')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ==================================================
                    TEMPLATE SELECT
                ================================================== */}

                {msgType === 'template' && (
                    <div className='fieldGroup'>
                        <label>Quick Template</label>

                        <select value={selectedTemplate} onChange={handleTemplateChange}>
                            <option value=''>Select Template</option>

                            <option value='trip'>Trip Details</option>

                            <option value='followup'>Follow Up</option>

                            <option value='payment'>Payment Reminder</option>

                            <option value='booking'>Booking Confirmation</option>

                            <option value='custom'>Custom</option>
                        </select>
                    </div>
                )}

                {/* ==================================================
                    MESSAGE
                ================================================== */}

                <div className='sendLabel'>Message</div>

                <div className='messageBox'>
                    <textarea
                        placeholder='Type your message here...'
                        value={message}
                        maxLength={1000}
                        onChange={(e) => setMessage(e.target.value)}
                    />

                    <div className='messageFooter'>
                        <div className='messageIcons'>😊 🖼️ 🔗</div>

                        <span>{message.length}/1000</span>
                    </div>
                </div>

                {/* ==================================================
                    FOOTER
                ================================================== */}

                <div className='sendModalFooter'>
                    <button className='cancelBtn' onClick={onClose}>
                        Cancel
                    </button>

                    <button
                        className='sendBtn'
                        onClick={onSend}
                        disabled={
                            !message?.trim() ||
                            loading ||
                            (sendType === 'selected' && selectedUsers.length === 0) ||
                            (sendType === 'filter' &&
                                (!broadcastFilters?.leadStatus?.length || filteredUserCount === 0))
                        }
                    >
                        🚀 Send Message
                    </button>
                </div>
            </div>
        </div>
    );
}
